import { baseApi } from "../baseApi";

const apiClientTag = "ApiClient";

/** An external company's OAuth credentials as returned by the list endpoint. */
export interface ApiClient {
  id: number;
  company_id: number;
  company_name: string | null;
  oauth_client_id: string;
  webhook_url: string | null;
  status: "active" | "suspended";
  createdAt: string;
}

export interface CreateApiClientPayload {
  company_id: number;
  company_name?: string;
  webhook_url?: string;
}

/**
 * The create response is the ONLY time the raw client_secret is ever returned —
 * the backend stores just a sha256 hash. It must be shown to the user once and
 * cannot be retrieved again.
 */
export interface CreateApiClientResponse {
  message: string;
  client: {
    id: number;
    company_id: number;
    company_name: string | null;
    client_id: string;
    client_secret: string;
    webhook_url: string | null;
    status: "active" | "suspended";
  };
}

export interface UpdateApiClientPayload {
  id: number;
  status?: "active" | "suspended";
  webhook_url?: string | null;
}

const apiClientApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getApiClients: builder.query<ApiClient[], void>({
      query: () => "/admin/api-clients",
      transformResponse: (response: ApiClient[] | { data: ApiClient[] }) =>
        Array.isArray(response) ? response : (response?.data ?? []),
      providesTags: [apiClientTag],
    }),

    createApiClient: builder.mutation<CreateApiClientResponse, CreateApiClientPayload>({
      query: (body) => ({
        url: "/admin/api-clients",
        method: "POST",
        body,
      }),
      invalidatesTags: [apiClientTag],
    }),

    updateApiClient: builder.mutation<unknown, UpdateApiClientPayload>({
      query: ({ id, ...body }) => ({
        url: `/admin/api-clients/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [apiClientTag],
    }),

    deleteApiClient: builder.mutation<unknown, number>({
      query: (id) => ({
        url: `/admin/api-clients/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [apiClientTag],
    }),
  }),
});

export const {
  useGetApiClientsQuery,
  useCreateApiClientMutation,
  useUpdateApiClientMutation,
  useDeleteApiClientMutation,
} = apiClientApi;
